import { useLocations, useModelBrowser } from '@/app/composable';
import router from '@/app/router';
import store from '@/app/store';
import Auth from '@/modules/auth/api/auth';
import { useOrganisation } from '@/modules/organization/composable';
import { UserStatus } from '@/modules/organization/constants';
import Keycloak from 'keycloak-js/lib/keycloak';
import * as R from 'ramda';
import { Route } from 'vue-router';
import { useErrors } from '@/app/composable/errors';

let keycloak: Keycloak;

export function useKeycloak() {
    const isEnabled = () => {
        return !R.isNil(keycloak);
    };

    const isAuthenticated = () => {
        return !!keycloak?.authenticated;
    };

    const getKeycloakToken = () => {
        return keycloak?.token;
    };

    const hasUserId = () => {
        return !!keycloak?.idTokenParsed?.userId;
    };

    const createAndInitializeKeycloak = async (homepage?: boolean) => {
        if (
            R.isNil(process.env.VUE_APP_KEYCLOAK_URL) ||
            R.isEmpty(process.env.VUE_APP_KEYCLOAK_URL) ||
            R.isNil(process.env.VUE_APP_KEYCLOAK_REALM) ||
            R.isEmpty(process.env.VUE_APP_KEYCLOAK_REALM) ||
            R.isNil(process.env.VUE_APP_KEYCLOAK_CLIENT_ID) ||
            R.isEmpty(process.env.VUE_APP_KEYCLOAK_CLIENT_ID)
        ) {
            // set as if keycloak has initialised (used for mock user)
            store.commit.auth.SET_KEYCLOAK(true);
            return;
        }

        if (!R.isNil(keycloak) && keycloak.didInitialize) return;

        keycloak = new Keycloak({
            url: process.env.VUE_APP_KEYCLOAK_URL || '',
            realm: process.env.VUE_APP_KEYCLOAK_REALM || '',
            clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID || '',
        });

        if (homepage) {
            await keycloak.init({ onLoad: 'check-sso', checkLoginIframe: false });
        } else {
            await keycloak.init({ onLoad: 'login-required', checkLoginIframe: false });
        }
        store.commit.auth.SET_KEYCLOAK(true);
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('login')) await afterLogin();
    };

    const login = async (to?: Route | null | undefined) => {
        if (!keycloak) return;
        try {
            if (!isAuthenticated()) await keycloak.login({ redirectUri: `${process.env.VUE_APP_URL}?login=true` });
            return await afterLogin(to);
        } catch (error) {
            console.error('Authentication process failed:', error);
            throw new Error('Authentication Failed');
        }
    };

    const afterLogin = async (to?: Route | null | undefined) => {
        Auth.keycloakLogin()
            .then(async (res: { data: { isNewUser: boolean } }) => {
                // The user's data has been synchronized with Keycloak.
                // Refresh the token to ensure it contains the latest user data.
                if (res.data.isNewUser) {
                    await createAndInitializeKeycloak();
                    await keycloak.updateToken(-1);
                }
                const { data } = await Auth.user();
                store.commit.auth.SET_USER(data);
                // Users that register for the first time in the platform without organization
                if (data.status === UserStatus.Pending && !data.organisationId) {
                    router.push({ name: 'organization-registration' });
                    // Users that have already register an organization and waiting for verification
                } else if (data.status === UserStatus.Pending && data.organisationId) {
                    router.push({ name: 'unverified-organization' });
                } else {
                    useModelBrowser().initialize();
                    useLocations().initialize();
                    if (data.organisationId) useOrganisation(data.organisationId).initialize();
                    store.dispatch.notificationEngine.fetchNotifications();
                    store.dispatch.executionVersions.loadVersions();
                    store.dispatch.executionErrors.loadExecutionErrors();
                    store.dispatch.dataModel.loadDomains();
                    if (process.env.VUE_APP_ETH_NODE)
                        store.dispatch.sharing.loadProviderAndNetwork(process.env.VUE_APP_ETH_NODE);
                }
                if (to && to.name && data.organisationId && data.status === UserStatus.Active) {
                    router.push({ name: to.name, params: to.params });
                }
            })
            .catch((err) => {
                if (err.response?.data) {
                    if (err.response.data.statusCode === 403) throw new Error('The account is not activated');
                    if (err.response.data.message) throw new Error(err.response.data.message);
                }
                if (err.message) throw new Error(err.message);
                console.log(new Error('Failed to login using keycloak'));
            });
    };

    const logout = async (redirectUri?: string) => {
        if (!keycloak) return;
        keycloak.logout({ redirectUri });
    };

    const register = async () => {
        if (!keycloak) return;

        // only initialize keycloak if user is not authenticated or if keycloak has not yet
        // initialized
        if (!isAuthenticated() && !keycloak.didInitialize) await keycloak.init({});
        await keycloak.register();
    };

    const getRefreshedToken = async () => {
        if (!keycloak) return;

        await keycloak.updateToken();
        return getKeycloakToken();
    };

    const refreshSocketToken = async () => {
        if (!keycloak) return;

        try {
            await keycloak.updateToken();
        } catch (error) {
            const { redirectToLogin } = useErrors();
            redirectToLogin();
            return false;
        }
    };

    return {
        hasUserId,
        isEnabled,
        isAuthenticated,
        login,
        logout,
        getKeycloakToken,
        register,
        getRefreshedToken,
        createAndInitializeKeycloak,
        refreshSocketToken,
    };
}
